import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Clock } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { CopyButton } from './CopyButton';
import type { ScriptSection } from '../../types/results';

interface ScriptSectionsViewProps {
  sections: ScriptSection[];
}

export function ScriptSectionsView({ sections }: ScriptSectionsViewProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const toggleSection = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  if (sections.length === 0) {
    return (
      <div className="text-center py-12 text-slate-400">
        No script sections found. Use Full Script view instead.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {sections.map((section, index) => {
        const isExpanded = expandedId === section.id;

        return (
          <motion.div
            key={section.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className="rounded-2xl bg-slate-900/50 backdrop-blur-xl border border-slate-700/50 overflow-hidden"
          >
            {/* Header - always visible */}
            <button
              onClick={() => toggleSection(section.id)}
              className="w-full flex items-center justify-between p-4 hover:bg-slate-800/30 transition-colors text-left"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-primary-500/10 flex items-center justify-center text-primary-400 text-sm font-bold">
                  {index + 1}
                </div>
                <div>
                  <h3 className="font-semibold text-slate-200">{section.title}</h3>
                  {section.timeEstimate && (
                    <div className="flex items-center gap-1.5 text-sm text-slate-500 mt-0.5">
                      <Clock className="w-3.5 h-3.5" />
                      <span>{section.timeEstimate}</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <CopyButton
                  text={section.content}
                  variant="small"
                  label="Copy"
                />
                <motion.div
                  animate={{ rotate: isExpanded ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <ChevronDown className="w-5 h-5 text-slate-400" />
                </motion.div>
              </div>
            </button>

            {/* Expandable content */}
            <AnimatePresence>
              {isExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="border-t border-slate-700/50"
                >
                  <div className="p-4">
                    <div className="prose prose-invert prose-sm max-w-none prose-headings:text-slate-100 prose-p:text-slate-300 prose-strong:text-slate-200 prose-li:text-slate-300">
                      <ReactMarkdown>{section.content}</ReactMarkdown>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        );
      })}
    </div>
  );
}
